import axios from 'axios';
import dayjs from 'dayjs';

import { API_ENDPOINTS } from 'Constants';
import { buildUrl } from 'Util/FetchUtils';
import { CharterClash } from 'Modules/Charter/Types/CharterClash';
import { CharterFormValues, charterBlock } from 'Modules/Charter/Types/CharterFormSchema';
import { CharterPayment, CharterScheduleItem } from 'Modules/Charter/Types/CharterScheduleItem';

/**
 * The charter endpoints.
 *
 * Dates cross the wire as local wall-clock with no timezone (`2026-08-20T09:00:00`), which is how
 * the server stores them and how a ferry trip's departure is already held. `toISOString` would
 * convert to UTC and land a charter on the wrong side of midnight for any operator not on UTC.
 */
const WIRE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const toWire = (date: Date) => dayjs(date).format(WIRE_FORMAT);

/**
 * The form's answers as the server takes them: a list of field id and answer, rather than the record
 * the form holds. An empty answer still goes — the server reads it as the field being cleared.
 */
const answersToWire = (dynamicFields: Record<string, string> | undefined) =>
	Object.entries(dynamicFields ?? {}).map(([fieldId, value]) => ({ fieldId, value }));

/** A charter as the server sends it — the same shape, with every date-time still a string. */
interface CharterResponse extends Omit<CharterScheduleItem, 'startDateTime' | 'endDateTime' | 'payment'> {
	startDateTime: string;
	endDateTime: string;
	payment?: Omit<CharterPayment, 'paidAt' | 'invoicedAt'> & { paidAt?: string; invoicedAt?: string };
}

/** The body of a 409, same shape as `CharterClash` with its dates still strings. */
interface CharterClashResponse {
	kind: CharterClash['kind'];
	name?: string;
	startDateTime: string;
	endDateTime?: string;
	status?: CharterClash['status'];
}

const fromWire = (charter: CharterResponse): CharterScheduleItem => ({
	...charter,
	startDateTime: dayjs(charter.startDateTime).toDate(),
	endDateTime: dayjs(charter.endDateTime).toDate(),
	payment: charter.payment
		? {
				...charter.payment,
				// Absent on an invoiced charter, and on any charter nobody has paid — invoicing is a
				// status change rather than a payment, so it never stamps one.
				paidAt: charter.payment.paidAt ? dayjs(charter.payment.paidAt).toDate() : undefined,
				invoicedAt: charter.payment.invoicedAt
					? dayjs(charter.payment.invoicedAt).toDate()
					: undefined,
			}
		: undefined,
});

/** Thrown when the ferry is already busy over the block asked for. Anything else propagates as-is. */
export class CharterClashError extends Error {
	readonly clash: CharterClash;

	constructor(clash: CharterClash) {
		super('The ferry is already busy then');
		this.name = 'CharterClashError';
		this.clash = clash;
	}
}

/**
 * The charters overlapping a window — including one that started before it and runs past its end,
 * which is on the ferry for the whole of it.
 */
export async function fetchCharters(startDate: Date, numOfDays: number): Promise<CharterScheduleItem[]> {
	const res = await axios.get<{ charters: CharterResponse[] }>(
		buildUrl(API_ENDPOINTS.CHARTERS.GET_SCHEDULE, {
			startDate: toWire(dayjs(startDate).startOf('day').toDate()),
			endDate: toWire(dayjs(startDate).startOf('day').add(numOfDays, 'days').toDate()),
		}),
	);

	return res.data.charters.map(fromWire);
}

/**
 * One charter, for the detail sidebar.
 *
 * Its own request rather than a read of the schedule's cache: the sidebar can be opened at an
 * address directly, and the week the schedule happens to hold may not be the week the charter is in.
 */
export async function fetchCharter(id: string): Promise<CharterScheduleItem> {
	const res = await axios.get<CharterResponse>(API_ENDPOINTS.CHARTERS.GET_ONE(id));

	return fromWire(res.data);
}

/** Which of a charter's two dates the staff-wide table's date range is measured against. */
export type CharterDateFilterType = 'created' | 'charter';

/** What the staff-wide charters table sends. Everything optional — an absent field is no filter. */
export interface ChartersListOptions {
	searchString?: string;
	userId?: string;
	startDate?: Date;
	endDate?: Date;
	dateFilterType?: CharterDateFilterType;
	/** Enum names — 'Reserved' | 'Booked' | 'Cancelled'. */
	statusFilters?: string[];
	ferryFilters?: string[];
	orderBy?: string;
	orderByDescending?: boolean;
	skip?: number;
	take?: number;
}

/**
 * A page of the charters table — every charter the operator has, or one customer's when `userId`
 * is given.
 *
 * The server does the filtering, the sorting and the paging and sends the matching total back
 * beside the page, because the staff-wide table is read against every charter ever taken.
 *
 * The date range crosses the wire as local wall-clock, like every other date a charter sends: a
 * charter's own block is stored with no timezone, and `toISOString` would move a range picked as
 * "the 3rd" onto the 2nd or the 4th for anybody not on UTC.
 */
export async function fetchChartersList(
	options: ChartersListOptions,
	/**
	 * Read the customer-scoped address instead — for a customer on /staff-bookings, who cannot reach
	 * `/api/charters` at all. The server decides whose charters those are; nothing here does.
	 */
	customerScoped = false,
): Promise<{ data: CharterScheduleItem[]; count: number }> {
	const params = new URLSearchParams();

	if (options.searchString) params.append('searchString', options.searchString);
	if (options.userId) params.append('userId', options.userId);
	if (options.startDate) params.append('startDate', toWire(dayjs(options.startDate).startOf('day').toDate()));
	if (options.endDate) params.append('endDate', toWire(dayjs(options.endDate).startOf('day').toDate()));
	if (options.dateFilterType) params.append('dateFilterType', options.dateFilterType);

	// Repeated per value rather than joined: that is how ASP.NET binds a `List<T>` off the query
	// string, and how the parking and bookings lists already send theirs.
	for (const status of options.statusFilters ?? []) params.append('statusFilters', status);
	for (const ferryId of options.ferryFilters ?? []) params.append('ferryFilters', ferryId);

	if (options.orderBy) params.append('orderBy', options.orderBy);
	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}
	if (options.skip !== undefined) params.append('skip', String(options.skip));
	if (options.take !== undefined) params.append('take', String(options.take));

	// Two addresses, one table. `/api/charters` is staff-only at the class level, so a customer on
	// /staff-bookings reads the customer-scoped twin instead — same query behind it, same shape back,
	// and the customer it answers for is whoever is asking rather than anything sent from here.
	const url = customerScoped ? API_ENDPOINTS.CHARTERS.GET_MINE_LIST : API_ENDPOINTS.CHARTERS.GET_LIST;

	const res = await axios.get<{ data: CharterResponse[]; count: number }>(`${url}?${params.toString()}`);

	return { data: res.data.data.map(fromWire), count: res.data.count };
}

/** The 409 body, in the shape the form reads. Shared by creating a charter and by editing one. */
const toClashError = (clash: CharterClashResponse) =>
	new CharterClashError({
		kind: clash.kind,
		name: clash.name,
		startDateTime: dayjs(clash.startDateTime).toDate(),
		endDateTime: clash.endDateTime ? dayjs(clash.endDateTime).toDate() : undefined,
		status: clash.status,
	});

/**
 * A refusal the server can answer a write with — a charter half filled in, one somebody else has
 * already booked, a booked one being edited. Carries the server's own message, which says which.
 */
export class CharterRefusedError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'CharterRefusedError';
	}
}

/**
 * Save a charter.
 *
 * Only called on values that have passed the form's schema, so both ends are there and readable.
 * The server runs the clash rules again over every charter and trip rather than the week the page
 * happens to have loaded, so a clash can come back here even though the form found none.
 *
 * `overrideClash` takes the ferry's time anyway. It is only ever set from the question the form asks
 * once a clash has been shown, so a charter saved over one has been agreed to twice — once here and
 * once by whoever pressed **Override clash**.
 */
export async function createCharter({
	values,
	overrideClash,
}: {
	values: CharterFormValues;
	overrideClash?: boolean;
}): Promise<CharterScheduleItem> {
	const block = charterBlock(values)!;

	try {
		const res = await axios.post<CharterResponse>(API_ENDPOINTS.CHARTERS.POST_CREATE, {
			name: values.name.trim() || null,
			ferryId: values.ferryId,
			startDateTime: toWire(block.start),
			endDateTime: toWire(block.end),
			userId: values.customer?.id ?? null,
			price: values.price ?? null,
			notes: values.notes.trim() || null,
			dynamicFields: answersToWire(values.dynamicFields),
			status: values.status,
			overrideClash: overrideClash ?? false,
		});

		return fromWire(res.data);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 409) {
				const clash = (error.response.data as { clash?: CharterClashResponse } | undefined)?.clash;

				if (clash) {
					throw toClashError(clash);
				}
			}

			if (error.response?.status === 400) {
				const message = (error.response.data as { message?: string } | undefined)?.message;

				if (message) {
					throw new CharterRefusedError(message);
				}
			}
		}

		throw error;
	}
}

/**
 * Change a reserved charter.
 *
 * Everything about a reserve is open, so this sends the whole form rather than a patch — the ferry
 * and the two ends included, which is why a clash can come back from an edit exactly as it can from
 * a create, and in the same shape.
 *
 * A booked charter is refused by the server. Nothing in the sidebar offers the edit on one.
 */
export async function updateCharter({
	id,
	values,
	overrideClash,
}: {
	id: string;
	values: CharterFormValues;
	/** Move it onto busy time anyway, once a manager has agreed to the clash. */
	overrideClash?: boolean;
}): Promise<CharterScheduleItem> {
	const block = charterBlock(values)!;

	try {
		const res = await axios.put<CharterResponse>(API_ENDPOINTS.CHARTERS.PUT_UPDATE(id), {
			name: values.name.trim() || null,
			ferryId: values.ferryId,
			startDateTime: toWire(block.start),
			endDateTime: toWire(block.end),
			userId: values.customer?.id ?? null,
			price: values.price ?? null,
			notes: values.notes.trim() || null,
			dynamicFields: answersToWire(values.dynamicFields),
			overrideClash: overrideClash ?? false,
		});

		return fromWire(res.data);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error.response?.status === 409) {
				const clash = (error.response.data as { clash?: CharterClashResponse } | undefined)?.clash;

				if (clash) {
					throw toClashError(clash);
				}
			}

			if (error.response?.status === 400) {
				const message = (error.response.data as { message?: string } | undefined)?.message;

				if (message) {
					throw new CharterRefusedError(message);
				}
			}
		}

		throw error;
	}
}

/**
 * Call off a charter, which hands the ferry back.
 *
 * The body carries one thing: what the operator keeps. A reserve owes nothing, so cancelling one
 * sends no fee and the server ignores the question. The server refuses a charter that is already
 * cancelled, and a fee larger than the charter's hire price, and says which.
 */
export async function cancelCharter(id: string, cancellationFee?: number): Promise<CharterScheduleItem> {
	try {
		const res = await axios.post<CharterResponse>(API_ENDPOINTS.CHARTERS.POST_CANCEL(id), {
			cancellationFee: cancellationFee ?? null,
		});

		return fromWire(res.data);
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 400) {
			const message = (error.response.data as { message?: string } | undefined)?.message;

			if (message) {
				throw new CharterRefusedError(message);
			}
		}

		throw error;
	}
}

/**
 * Mark a charter as handed to accounting, or put it back.
 *
 * One call for both directions because the sidebar offers one action that reads either way, and the
 * two endpoints differ only in which way they set the mark.
 *
 * The mark is not a payment. A charter that has been marked still reads **Awaiting** — an invoice
 * raised in the accounting system is settled there, the way a booking's is. What it changes is the
 * next invoice report export, which leaves a marked charter out.
 *
 * The server refuses a charter that is not booked, and one with no invoice to raise, and says which.
 */
export async function setCharterInvoiced({
	id,
	invoiced,
}: {
	id: string;
	invoiced: boolean;
}): Promise<CharterScheduleItem> {
	const url = invoiced
		? API_ENDPOINTS.CHARTERS.POST_MARK_INVOICED(id)
		: API_ENDPOINTS.CHARTERS.POST_UNDO_INVOICED(id);

	try {
		const res = await axios.post<CharterResponse>(url);

		return fromWire(res.data);
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 400) {
			const message = (error.response.data as { message?: string } | undefined)?.message;

			if (message) {
				throw new CharterRefusedError(message);
			}
		}

		throw error;
	}
}

/**
 * Confirm a reserved charter into a booked one.
 *
 * The customer and the price go with it because a reserve is routinely taken before either is known
 * — confirming it is what settles them. Sending neither leaves what the charter already carries, and
 * the server refuses the booking if that still adds up to nothing.
 *
 * There is no call the other way. A booking cannot be walked back to a reserve, and it cannot be
 * cancelled either — `cancelCharter` is for a reserve that is being given up before anybody
 * committed to it.
 */
export async function bookCharter({
	id,
	userId,
	price,
}: {
	id: string;
	userId?: string;
	price?: number;
}): Promise<CharterScheduleItem> {
	try {
		const res = await axios.post<CharterResponse>(API_ENDPOINTS.CHARTERS.POST_BOOK(id), {
			userId: userId ?? null,
			price: price ?? null,
		});

		return fromWire(res.data);
	} catch (error) {
		if (axios.isAxiosError(error) && error.response?.status === 400) {
			const message = (error.response.data as { message?: string } | undefined)?.message;

			if (message) {
				throw new CharterRefusedError(message);
			}
		}

		throw error;
	}
}
